import * as typed from "./typed-arrays.js";

/**
 * LRUCache.
 *
 * @constructor
 * @param {number}   capacity - Desired capacity.
 */
export default function LRUCache(capacity) {
    this.capacity = capacity;
    if (typeof this.capacity !== 'number' || this.capacity <= 0)
        throw new Error('mnemonist/lru-cache: capacity should be positive number.');
    else if (!isFinite(this.capacity) || Math.floor(this.capacity) !== this.capacity)
        throw new Error('mnemonist/lru-cache: capacity should be a finite positive integer.');
    var PointerArray = typed.getPointerArray(capacity);
    this.forward = new PointerArray(capacity);
    this.backward = new PointerArray(capacity);
    this.K = new Array(capacity);
    this.V = new Array(capacity);
    // Properties
    this.size = 0;
    this.head = 0;
    this.tail = 0;
    this.items = {};
}

/**
 * Method used to clear the structure.
 *
 * @return {undefined}
 */
LRUCache.prototype.clear = function () {
    this.size = 0;
    this.head = 0;
    this.tail = 0;
    this.items = {};
};

/**
 * Method used to splay a value on top.
 *
 * @param  {number}   pointer - Pointer of the value to splay on top.
 * @return {LRUCache}
 */
LRUCache.prototype.splayOnTop = function (pointer) {
    var oldHead = this.head;
    if (this.head === pointer)
        return this;
    var previous = this.backward[pointer], next = this.forward[pointer];
    if (this.tail === pointer) {
        this.tail = previous;
    }
    else {
        this.backward[next] = previous;
    }
    this.forward[previous] = next;
    this.backward[oldHead] = pointer;
    this.head = pointer;
    this.forward[pointer] = oldHead;
    return this;
};

/**
 * Method used to set the value for the given key in the cache.
 *
 * @param  {any} key   - Key.
 * @param  {any} value - Value.
 * @return {undefined}
 */
LRUCache.prototype.set = function (key, value) {
    var pointer = this.items[key];
    // The key already exists, we just need to update the value and splay on top
    if (typeof pointer !== 'undefined') {
        this.splayOnTop(pointer);
        this.V[pointer] = value;
        return;
    }
    // The cache is not yet full
    if (this.size < this.capacity) {
        pointer = this.size++;
    }
    // Cache is full, we need to drop the last value
    else {
        pointer = this.tail;
        this.tail = this.backward[pointer];
        delete this.items[this.K[pointer]];
    }
    // Storing key & value
    this.items[key] = pointer;
    this.K[pointer] = key;
    this.V[pointer] = value;
    // Moving the item at the front of the list
    this.forward[pointer] = this.head;
    this.backward[this.head] = pointer;
    this.head = pointer;
};

/**
 * Method used to set the value for the given key in the cache
 *
 * @param  {any} key   - Key.
 * @param  {any} value - Value.
 * @return {{evicted: boolean, key: any, value: any}} An object containing the
 * key and value of an item that was overwritten or evicted in the set
 * operation, as well as a boolean indicating whether it was evicted due to
 * limited capacity. Return value is null if nothing was evicted or overwritten
 * during the set operation.
 */

LRUCache.prototype.setpop = function (key, value) {
    var oldValue = null;
    var oldKey = null;
    var pointer = this.items[key];
    // The key already exists, we just need to update the value and splay on top
    if (typeof pointer !== 'undefined') {
        this.splayOnTop(pointer);
        oldValue = this.V[pointer];
        this.V[pointer] = value;
        return { evicted: false, key: key, value: oldValue };
    }
    // The cache is not yet full
    if (this.size < this.capacity) {
        pointer = this.size++;
    }
    // Cache is full, we need to drop the last value
    else {
        pointer = this.tail;
        this.tail = this.backward[pointer];
        oldValue = this.V[pointer];
        oldKey = this.K[pointer];
        delete this.items[oldKey];
    }
    // Storing key & value
    this.items[key] = pointer;
    this.K[pointer] = key;
    this.V[pointer] = value;
    // Moving the item at the front of the list
    this.forward[pointer] = this.head;
    this.backward[this.head] = pointer;
    this.head = pointer;
    // Return object if eviction took place, otherwise return null
    if (oldKey) {
        return { evicted: true, key: oldKey, value: oldValue };
    }
    else {
        return null;
    }
};

/**
 * Method used to check whether the key exists in the cache.
 *
 * @param  {any} key   - Key.
 * @return {boolean}
 */
LRUCache.prototype.has = function (key) {
    return key in this.items;
};

/**
 * Method used to get the value attached to the given key. Will move the
 * related key to the front of the underlying linked list.
 *
 * @param  {any} key   - Key.
 * @return {any}
 */
LRUCache.prototype.get = function (key) {
    var pointer = this.items[key];
    if (typeof pointer === 'undefined')
        return;
    this.splayOnTop(pointer);
    return this.V[pointer];
};

/**
 * Method used to get the value attached to the given key. Does not modify
 * the ordering of the underlying linked list.
 *
 * @param  {any} key   - Key.
 * @return {any}
 */
LRUCache.prototype.peek = function (key) {
    var pointer = this.items[key];
    if (typeof pointer === 'undefined')
        return;
    return this.V[pointer];
};
